















































































import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch
} from '@vue/composition-api';
import { AxiosError } from 'axios';
import { useCreateTax, useUpdateTax } from '@/composable/tax';

import { cloneDeep } from 'lodash';
import { diffDeep, setErrorFromAxiosError } from '@/utils/helpers';
import { ITax, ITaxData, Tax } from '@/models';
import { ISystemError } from '@/models/error/error.types';
import { ALForm } from '@/common/component.types';

import ErrorAlert from '@/components/common/ErrorAlert.vue';
import ALDialog from '@/components/core/Dialog/ALDialog.vue';

export default defineComponent({
  name: 'TaxFormDialog',
  components: {
    ALDialog,
    ErrorAlert
  },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    tax: {
      type: Object as PropType<ITaxData | null>,
      default: null
    }
  },
  emits: ['update:dialog'],
  setup(props, { emit, root }) {
    const localDialog = computed({
      get: () => props.dialog,
      set: value => {
        emit('update:dialog', value);
      }
    });

    const taxData = ref<ITaxData>({});

    watch(localDialog, value => {
      if (value) {
        resetFormValidation();
        taxData.value = !props.isEdit
          ? ({} as ITaxData)
          : cloneDeep(props.tax!);
      }
    });

    const formRef = ref<ALForm | null>(null);
    const valid = ref(false);

    const subtitle = computed(
      () => `${props.isEdit ? 'Update' : 'Create'} tax information`
    );

    function closeDialog() {
      clearErrors();
      localDialog.value = false;
    }

    const {
      isLoading: isUpdatePending,
      error: updateError,
      mutate: updateTax,
      reset: updateReset
    } = useUpdateTax();
    const {
      isLoading: isCreatePending,
      error: createError,
      mutate: createTax,
      reset: createReset
    } = useCreateTax();

    const localError = ref<ISystemError | null>(null);

    const loading = computed(() =>
      props.isEdit ? isUpdatePending.value : isCreatePending.value
    );

    const error = computed<ISystemError | null>({
      get() {
        let systemError: ISystemError | null = localError.value;
        const axiosError = props.isEdit ? updateError.value : createError.value;

        if (axiosError) {
          systemError = setErrorFromAxiosError(axiosError as AxiosError);
        }

        return systemError;
      },
      set(value: ISystemError | null) {
        localError.value = value;
      }
    });

    function clearErrors() {
      props.isEdit ? updateReset() : createReset();
      error.value = null;
    }

    function resetFormValidation(): void {
      if (formRef.value) {
        return formRef.value.resetValidation();
      }
    }

    function validateForm(): boolean {
      let valid = false;
      if (formRef.value) {
        valid = formRef.value.validate();
      }
      return valid;
    }

    async function save() {
      clearErrors();
      const valid = await validateForm();

      if (valid) {
        let taxInstance: ITax | null = null;

        try {
          taxInstance = new Tax(taxData.value);
        } catch (e) {
          // When constructing the instance before saving the possibility of an error can occur the local error is to store that error instance and display it
          error.value = e as ISystemError;
        }

        if (taxInstance != null) {
          const saveAction = props.isEdit ? updateTax : createTax;

          let payload = taxInstance;
          if (props.isEdit) {
            payload = diffDeep(taxInstance, props.tax, true);
          }

          saveAction(payload, {
            onSuccess: () => {
              closeDialog();
            }
          });
        }
      }
    }

    return {
      localDialog,
      taxData,
      valid,
      formRef,
      subtitle,
      closeDialog,
      save,
      clearErrors,
      loading,
      error
    };
  }
});
